import config from 'src/shared/config';
import { pollAgentRequest } from './agentRequestPolling';
import {
    AgentAttachment,
    AgentTurn,
    AgentConversation,
    AgentConversationList,
    AgentDraft,
    AgentDraftEditResponse,
    AgentGesture,
    AgentMessageRequest,
    AgentTurnRequest,
    AgentQuestionnaireSubmission,
    DeleteAgentPrintAreaInput,
    SetAgentSideScaleInput,
    ReplaceAgentSideImageInput,
    UpsertAgentPrintAreaInput,
    RenamedAgentConversation
} from './types';

export class AgentApiError extends Error {
    constructor(
        message: string,
        public readonly status: number,
        /** From the Retry-After header, when the server asks to wait before trying again. */
        public readonly retryAfterSeconds: number | null = null,
        public readonly code: string | null = null,
        public readonly requirements: AgentTurn['operationRequirements'] = null
    ) {
        super(message);
        this.name = 'AgentApiError';
    }
}

const apiUrl = (path: string) => `${config.apiUrl?.replace(/\/$/, '') ?? ''}/${path.replace(/^\//, '')}`;

const readErrorMessage = async (response: Response) => {
    const fallback = `${response.status} ${response.statusText}`.trim();
    const contentType = response.headers.get('content-type') ?? '';

    try {
        if (contentType.includes('application/json')) {
            const body = (await response.json()) as {
                code?: string;
                requirements?: AgentTurn['operationRequirements'];
                message?: string;
                detail?: string;
                title?: string;
            };
            return {
                message: body.detail || body.message || body.title || fallback,
                code: typeof body.code === 'string' ? body.code : null,
                requirements: Array.isArray(body.requirements) ? body.requirements.filter((item) => item && typeof item.code === 'string') : null
            };
        }

        return { message: (await response.text()).slice(0, 300) || fallback, code: null, requirements: null };
    } catch {
        return { message: fallback, code: null, requirements: null };
    }
};

const request = async <T>(path: string, init: RequestInit = {}, allowNotFound = false): Promise<T | null> => {
    const response = await fetch(apiUrl(path), {
        ...init,
        headers: {
            Authorization: `Bearer ${config.token}`,
            Accept: 'application/json',
            'Accept-Language': config.culture,
            ...(init.body ? { 'Content-Type': 'application/json' } : {}),
            ...init.headers
        }
    });

    if (allowNotFound && response.status === 404) return null;

    if (!response.ok) {
        const retryAfter = Number(response.headers.get('retry-after'));
        const error = await readErrorMessage(response);
        throw new AgentApiError(
            error.message,
            response.status,
            Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : null,
            error.code,
            error.requirements
        );
    }

    if (response.status === 204) return null;
    return (await response.json()) as T;
};

export const createAgentMessageIdempotencyKey = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export const createAgentConversation = async (name: string, signal?: AbortSignal) => {
    return (await request<AgentConversation>('v1/agent/conversations', {
        method: 'POST',
        body: JSON.stringify({ name }),
        signal
    })) as AgentConversation;
};

export const getAgentConversation = async (conversationId: number, signal?: AbortSignal) => {
    return request<AgentConversation>(`v1/agent/conversations/${conversationId}`, { signal }, true);
};

export const listAgentConversations = async (pageNumber = 1, pageSize = 20, signal?: AbortSignal) => {
    return (await request<AgentConversationList>(`v1/agent/conversations?pageNumber=${pageNumber}&pageSize=${pageSize}`, { signal })) as AgentConversationList;
};

export const renameAgentConversation = async (conversationId: number, name: string, signal?: AbortSignal) => {
    return (await request<RenamedAgentConversation>(`v1/agent/conversations/${conversationId}/name`, {
        method: 'PUT',
        body: JSON.stringify({ name }),
        signal
    })) as RenamedAgentConversation;
};

export const deleteAgentConversation = async (conversationId: number, signal?: AbortSignal) => {
    await request<void>(`v1/agent/conversations/${conversationId}`, { method: 'DELETE', signal });
};

export const getAgentDraft = async (conversationId: number, signal?: AbortSignal) => {
    return request<AgentDraft>(`v1/agent/conversations/${conversationId}/draft`, { signal }, true);
};

export const getAgentTurnDraft = async (conversationId: number, turnId: number, signal?: AbortSignal) => {
    return request<AgentDraft>(`v1/agent/conversations/${conversationId}/turns/${turnId}/draft`, { signal }, true);
};

export const upsertAgentPrintArea = async (conversationId: number, input: UpsertAgentPrintAreaInput) => {
    return (await request<AgentDraftEditResponse>(`v1/agent/conversations/${conversationId}/draft/product/print-areas`, {
        method: 'POST',
        body: JSON.stringify(input)
    })) as AgentDraftEditResponse;
};

export const replaceAgentSideImage = async (conversationId: number, input: ReplaceAgentSideImageInput) => {
    return (await request<AgentDraftEditResponse>(`v1/agent/conversations/${conversationId}/draft/product/side-image`, {
        method: 'PUT',
        body: JSON.stringify(input)
    })) as AgentDraftEditResponse;
};

export const deleteAgentPrintArea = async (conversationId: number, input: DeleteAgentPrintAreaInput) => {
    return (await request<AgentDraftEditResponse>(`v1/agent/conversations/${conversationId}/draft/product/print-areas`, {
        method: 'DELETE',
        body: JSON.stringify(input)
    })) as AgentDraftEditResponse;
};

export const restoreAgentPrintArea = async (conversationId: number, input: DeleteAgentPrintAreaInput) => {
    return (await request<AgentDraftEditResponse>(`v1/agent/conversations/${conversationId}/draft/product/print-areas/restore`, {
        method: 'POST',
        body: JSON.stringify(input)
    })) as AgentDraftEditResponse;
};

export const setAgentSideScale = async (conversationId: number, input: SetAgentSideScaleInput) => {
    return (await request<AgentDraftEditResponse>(`v1/agent/conversations/${conversationId}/draft/product/side-scale`, {
        method: 'PUT',
        body: JSON.stringify(input)
    })) as AgentDraftEditResponse;
};

export const postAgentMessage = async (
    conversationId: number,
    message: string,
    attachments: AgentAttachment[],
    gesture: AgentGesture | null,
    idempotencyKey: string = createAgentMessageIdempotencyKey(),
    signal?: AbortSignal,
    answers?: AgentQuestionnaireSubmission,
    zendeskArticleId?: string
) => {
    const body: AgentMessageRequest = {
        message: message || null,
        attachments: attachments.length > 0 ? attachments : null,
        gesture,
        answers,
        zendeskArticleId: zendeskArticleId || null
    };

    return (await request<AgentTurnRequest>(`v1/agent/conversations/${conversationId}/messages`, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: { 'Idempotency-Key': idempotencyKey },
        signal
    })) as AgentTurnRequest;
};

export const cancelAgentRequest = async (conversationId: number, requestId: number) =>
    (await request<AgentTurnRequest>(`v1/agent/conversations/${conversationId}/requests/${requestId}/cancel`, {
        method: 'POST'
    })) as AgentTurnRequest;

export const waitForAgentTurn = async (conversationId: number, requestId: number, signal?: AbortSignal, onProgress?: (request: AgentTurnRequest) => void) =>
    pollAgentRequest(
        async () => (await request<AgentTurnRequest>(`v1/agent/conversations/${conversationId}/requests/${requestId}`, { signal })) as AgentTurnRequest,
        signal,
        onProgress
    );
